import { ProjectElementForImport } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import { useMemo } from 'react'
import { useShallowActionBarStoreSelector } from '../ActionBarStore'
import type { ActionBarStoreType } from '../types/types'

/** Supported panel types for element list management */
export enum PanelType {
    ASSEMBLIES = 'ASSEMBLIES',
    DIVISIONS = 'DIVISIONS', 
    SUSTAINABILITY = 'SUSTAINABILITY',
    DEFAULT = 'DEFAULT',
}

/** Return type for the usePanelElementList hook */
export interface PanelElementListHook {
    /** Current element list for the panel */
    elementList: ProjectElementForImport[] | null
    /** Function to update the element list */
    setElementList: (elementList: ProjectElementForImport[] | null) => void
}

/** Valid panel types for runtime validation */
const VALID_PANEL_TYPES: readonly PanelType[] = [
    PanelType.ASSEMBLIES,
    PanelType.DIVISIONS,
    PanelType.SUSTAINABILITY,
    PanelType.DEFAULT,
] as const

/**
 * Hook to get isolated element list and setter for specific panel types.
 * Solves state sharing issues between multiple panel instances.
 *
 * @param panelType - Panel type (ASSEMBLIES, DIVISIONS, SUSTAINABILITY, DEFAULT)
 * @returns Element list and setter function for the specified panel type
 */
export const usePanelElementList = (
    panelType?: PanelType
): PanelElementListHook => {
    const isPanelType = (value: unknown): value is PanelType => {
        return (
            typeof value === 'string' &&
            VALID_PANEL_TYPES.includes(value as PanelType)
        )
    }

    // Validate panel type and provide fallback
    const validatedPanelType = useMemo(() => {
        if (!isPanelType(panelType)) {
            return PanelType.DEFAULT
        }

        if (!isPanelType(panelType)) {
            console.warn(
                `[usePanelElementList] Unknown panel type: "${panelType}". Falling back to "DEFAULT". ` +
                    `Valid types are: ${VALID_PANEL_TYPES.join(', ')}`
            )
            return PanelType.DEFAULT
        }

        return panelType
    }, [panelType])

    // Use shallow selector to only subscribe to the specific data we need
    const { elementList, setElementList } = useShallowActionBarStoreSelector<{
        elementList: ProjectElementForImport[] | null
        setElementList: (elementList: ProjectElementForImport[] | null) => void
    }>((state: ActionBarStoreType) => {
            switch (validatedPanelType) {
                case PanelType.ASSEMBLIES:
                    return {
                        elementList: state.assemblyElementList,
                        setElementList: state.actions.setAssemblyElementList,
                    }
                case PanelType.DIVISIONS:
                    return {
                        elementList: state.divisionElementList,
                        setElementList: state.actions.setDivisionElementList,
                    }
                case PanelType.SUSTAINABILITY:
                    return {
                        elementList: state.sustainabilityElementList,
                        setElementList:
                            state.actions.setSustainabilityElementList,
                    }
                case PanelType.DEFAULT:
                default:
                    return {
                        elementList: state.elementList,
                        setElementList: state.actions.setElementList,
                    }
            }
        }
    )

    return { elementList, setElementList }
}
